import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card } from "./ui";
import { AlertTriangle, CalendarClock } from "lucide-react";

// Group-HQ widget: option/bond contracts expiring within 30 days (or already
// expired) aggregated across every entity. Renders nothing when there's nothing
// to watch, so it only appears when action may be needed.
export default function ExpiringContractsWidget() {
  const { t, lang } = useApp();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);

  useEffect(() => {
    let active = true;
    api.get("/portfolio/expiring?days=30")
      .then(({ data }) => { if (active) setItems(data); })
      .catch(() => { if (active) setItems([]); });
    return () => { active = false; };
  }, []);

  if (!items || items.length === 0) return null;

  const fmtDate = (d) => {
    try {
      return new Date(d).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return d; }
  };
  const detail = (c) => {
    if (c.type === "option") {
      const bits = [];
      if (c.option_side) bits.push(c.option_side.toUpperCase());
      if (c.strike != null) bits.push(`${t("activity_strike")} ${c.strike}`);
      return bits.join(" · ");
    }
    return c.coupon != null ? `${t("portfolio_coupon")} ${c.coupon}%` : "";
  };

  return (
    <Card className="mb-6" data-testid="expiring-contracts-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CalendarClock className="w-4 h-4" style={{ color: "#F59E0B" }} />
          <h3 className="font-heading font-medium">{t("hq_expiring_title")}</h3>
        </div>
        <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-medium"
          style={{ color: "#F59E0B", background: "#F59E0B1A" }} data-testid="expiring-count">
          <AlertTriangle className="w-3 h-3" /> {items.length}
        </span>
      </div>
      <div className="space-y-1">
        {items.map((c, i) => {
          const expired = c.expired;
          const color = expired ? "#F87171" : "#FBBF24";
          return (
            <button key={`${c.entity_id}-${c.symbol}-${i}`}
              onClick={() => navigate(`/entity/${c.entity_key}?tab=holdings`)}
              data-testid={`expiring-row-${i}`}
              className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-surface-hover transition-colors text-left">
              <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${color}22` }}>
                <CalendarClock className="w-4 h-4" style={{ color }} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium truncate">{c.name || c.symbol}</span>
                  <span className="inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full font-medium shrink-0"
                    style={{ backgroundColor: `${c.entity_color || "#2563EB"}22`, color: c.entity_color || "#2563EB" }}>
                    {c.entity_name}
                  </span>
                </div>
                <div className="text-[11px] text-text-secondary truncate">
                  {c.symbol && <span className="font-mono">{c.symbol}</span>}
                  {detail(c) ? ` · ${detail(c)}` : ""}
                  {` · ${fmtDate(c.expiration)}`}
                </div>
              </div>
              <span className="text-xs font-medium tabular-nums shrink-0" style={{ color }} data-testid={`expiring-days-${i}`}>
                {expired
                  ? t("portfolio_expired")
                  : `${t("hq_expiring_in")} ${c.days_to_expiry}${t("portfolio_days_short")}`}
              </span>
            </button>
          );
        })}
      </div>
    </Card>
  );
}
